import { useEffect, useRef, useState } from "react";
import "./Cargocommitment.css";

/**
 * CargoCommitment
 * -----------------------------------------------------------------------
 * Motto section for a lorry / freight transport site: "Your Cargo, Our Commitment"
 *
 * Signature element: an animated "route line" — a dashed highway line that
 * draws itself in, with a truck marker travelling across it and three
 * waypoints (Pickup -> In Transit -> Delivered) lighting up in sequence.
 * It's a literal, on-brand stand-in for the promise being made in the copy.
 *
 * No external UI libraries required. Drop the two files into your project
 * and import <CargoCommitment /> wherever the section should render.
 *
 * Replace the placeholder numbers in STATS with your real fleet data.
 * -----------------------------------------------------------------------
 */

const STATS = [
  { value: "500+", label: "Trips completed monthly" },
  { value: "98.4%", label: "On-time delivery rate" },
  { value: "24/7", label: "Dispatch & live tracking" },
];

export default function CargoCommitment() {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`cc-section ${isVisible ? "is-visible" : ""}`}
      aria-label="Our commitment"
    >
      <div className="cc-bg-truck" aria-hidden="true">
        <svg viewBox="0 0 400 220" fill="none">
          <path
            d="M10 150h170v-70a10 10 0 0 1 10-10h40l50 50v30h20M180 80v70"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <circle cx="90" cy="155" r="18" stroke="currentColor" strokeWidth="4" />
          <circle cx="250" cy="155" r="18" stroke="currentColor" strokeWidth="4" />
        </svg>
      </div>

      <div className="cc-inner">
        <p className="cc-eyebrow">
          <span className="cc-eyebrow-dot" aria-hidden="true" />
          Route status &mdash; active
        </p>

        <h2 className="cc-headline">
          <span className="cc-headline-line">Your Cargo,</span>
          <span className="cc-headline-line cc-headline-accent">
            Our Commitment.
          </span>
        </h2>

        <p className="cc-subtext">
          Every consignment tracked, every deadline held. From loading dock to
          final drop, we move freight the way we&apos;d want our own moved.
        </p>

        {/* Signature: animated route line */}
        <div className="cc-route" role="img" aria-label="Shipment journey from pickup to delivery">
          <svg
            className="cc-route-svg"
            viewBox="0 0 1000 60"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <line
              className="cc-route-track"
              x1="0"
              y1="30"
              x2="1000"
              y2="30"
            />
            <line
              className="cc-route-progress"
              x1="0"
              y1="30"
              x2="1000"
              y2="30"
            />
          </svg>

          <div className="cc-truck-marker" aria-hidden="true">
            <svg viewBox="0 0 48 30" fill="none">
              <rect x="1" y="8" width="26" height="14" rx="1.5" stroke="currentColor" strokeWidth="2.5" />
              <path
                d="M27 12h10l8 7v3h-18z"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinejoin="round"
              />
              <circle cx="11" cy="24" r="4" fill="currentColor" />
              <circle cx="35" cy="24" r="4" fill="currentColor" />
            </svg>
          </div>

          <div className="cc-waypoints">
            <span className="cc-waypoint cc-waypoint-1">
              <span className="cc-waypoint-dot" />
              Pickup
            </span>
            <span className="cc-waypoint cc-waypoint-2">
              <span className="cc-waypoint-dot" />
              In transit
            </span>
            <span className="cc-waypoint cc-waypoint-3">
              <span className="cc-waypoint-dot" />
              Delivered
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}